import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import notificationService from '../services/notificationService';
import useNotificationSound from '../hooks/useNotificationSound';
import { toast } from 'react-toastify';
import NOTIFICATION_CONFIG from '../config/notificationConfig';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { playSound } = useNotificationSound();

  // Fetch all notifications
  const fetchNotifications = useCallback(async (page = 0, size = 10) => {
    setLoading(true);
    try {
      const response = await notificationService.getAllNotifications();
      setNotifications(response || []);

      // Calculate unread count from the combined notifications list
      const unread = (response || []).filter(n => !n.read).length;
      setUnreadCount(unread);

      setError(null);
    } catch (err) {
      setError('Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread notifications and set unread count
  const fetchUnreadNotifications = useCallback(async () => {
    try {
      // Use fetchNotifications instead to get both API and local unread counts
      await fetchNotifications();
    } catch (err) {
      console.error('Error fetching unread notifications:', err);
    }
  }, [fetchNotifications]);

  // Handle reading a notification
  const handleMarkAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);

      // Update local state to reflect the change
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );

      // Update unread count
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));

      // Force refetch to ensure proper state sync with backend
      setTimeout(fetchUnreadNotifications, 300);
    } catch (err) {
      toast.error('Failed to mark notification as read');
      console.error('Error marking notification as read:', err);
    }
  }, [fetchUnreadNotifications]);

  // Handle marking all notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();

      // Update local state to reflect all notifications as read
      setNotifications(prevNotifications =>
        prevNotifications.map(notification => ({ ...notification, read: true }))
      );

      // Reset unread count
      setUnreadCount(0);

      // Force refetch to ensure proper state sync with backend
      setTimeout(fetchUnreadNotifications, 300);

      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all notifications as read');
      console.error('Error marking all notifications as read:', err);
    }
  }, [fetchUnreadNotifications]);

  // Handle deleting a notification
  const handleDeleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);

      // Remove the deleted notification from local state
      setNotifications(prevNotifications =>
        prevNotifications.filter(notification => notification.id !== notificationId)
      );

      // Update unread count if the deleted notification was unread
      const deletedNotification = notifications.find(n => n.id === notificationId);
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }

      toast.success('Notification deleted successfully');
    } catch (err) {
      toast.error('Failed to delete notification');
      console.error('Error deleting notification:', err);
    }
  }, [notifications]);

  // Refresh notification data periodically
  useEffect(() => {
    fetchNotifications();
    fetchUnreadNotifications();

    // Use configurable polling frequency to reduce server load
    const intervalId = setInterval(() => {
      fetchUnreadNotifications();
    }, NOTIFICATION_CONFIG.pollingInterval);

    // Listen for immediate notifications from order creation
    const handleNewNotification = (event) => {
      const newNotification = event.detail;
      console.log('Received immediate notification:', newNotification);

      // Add to notifications list
      setNotifications(prevNotifications => [newNotification, ...prevNotifications]);

      // Increment unread count
      setUnreadCount(prevCount => prevCount + 1);

      // Play sound immediately
      // playSound();

      // Show toast notification
      // toast.success(`${newNotification.title}: ${newNotification.message}`);
    };

    window.addEventListener('newNotification', handleNewNotification);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('newNotification', handleNewNotification);
    };
  }, [fetchNotifications, fetchUnreadNotifications, playSound]);

  // Play sound when unread count increases
  useEffect(() => {
    if (unreadCount > 0) {
      playSound();
    }
  }, [unreadCount, playSound]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadNotifications,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    deleteNotification: handleDeleteNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext; 